/**
 * The KalmTide reference renderer — THE conformance oracle.
 *
 * Renders a validated kalmtide/1 Preset to stereo float PCM, deterministic
 * to the sample. The Kotlin engine (android/core-dsp) must reproduce this
 * output within conformance thresholds; where algorithms are cheap to mirror
 * (oscillators, noise, limiter) it should match sample-exactly.
 *
 * Contract decisions encoded here (mirror in Kotlin, document drift in CI):
 * - Time of sample n = n / sampleRate (double). Sample n belongs to the
 *   segment whose [t0, t1) contains that time.
 * - Oscillator phase = closed-form integral of frequency (see curves.ts):
 *   phase-continuous across boundaries, block-size invariant.
 * - Binaural: left = shape(phi_c - phi_b/2), right = shape(phi_c + phi_b/2).
 *   Monaural: 0.5 * (left-tone + right-tone) to both channels.
 *   Isochronic: carrier gated by raised-cosine window over the open dutyCycle
 *   fraction of each beat period: g(theta) = 0.5*(1 - cos(2*pi*theta/duty))
 *   for theta < duty, else 0 (theta = frac(beat phase)).
 * - Noise: xoshiro128++ seeded from fnv1a32(layer.id). white = raw uniform;
 *   pink = Paul Kellett filter; brown = leaky integrator
 *   b = (b + 0.02*w)/1.02, out = 3.5*b.
 * - A noise envelope, when present, REPLACES the layer's static gainDb
 *   (absolute dB automation; value held before first and after last point;
 *   linear-in-dB interpolation between points). <= -120 dB is exact silence.
 * - Pan is a linear balance law: gainL = min(1, 1-pan), gainR = min(1, 1+pan).
 * - Ducking: while a voice asset plays, targeted layer types are attenuated
 *   by duckDb with 100 ms linear-in-dB ramps at both edges; overlapping voices
 *   don't stack — the deepest duck wins.
 * - Voice repeats: with repeatEverySec R, instance k (k = 0, 1, ...) starts at
 *   frame round(startSec*sr) + k*round(R*sr), for every k whose start frame is
 *   < totalFrames. Instances mix additively (overlaps sum, k ascending).
 *   Ducking windows repeat at startSec + k*R seconds for the same instance
 *   count; a layer's own overlapping instances merge like separate voices
 *   (deepest duck wins).
 * - Master: gain, then linear-amplitude fade in/out, then a stereo-linked
 *   peak limiter (instant attack, 50 ms release, ceiling -1 dBFS). The
 *   ceiling is a hard guarantee: no output sample can exceed it.
 * - Assets (ambience/voice) are resolved by an injected AssetResolver and
 *   must already be at the engine sample rate (resampling happens at load
 *   time, outside the render loop). Missing assets throw at construction —
 *   deterministic failure, never silent fallback.
 */

import type {
  Preset,
  Layer,
  SegmentedLayer,
  NoiseLayer,
  AmbienceLayer,
  VoiceLayer,
  LayerType,
  Waveform,
} from "../../schema/types.ts";
import { isSegmentedLayer } from "../../schema/types.ts";
import {
  DEFAULT_SAMPLE_RATE,
  LIMITER_CEILING,
  LIMITER_RELEASE_SEC,
  DUCK_RAMP_SEC,
  SILENCE_FLOOR_DB,
} from "./constants.ts";
import { compileRamp, frac, type CompiledRamp } from "./curves.ts";
import { fnv1a32, Xoshiro128pp } from "./rng.ts";

export interface StereoBuffer {
  left: Float32Array;
  right: Float32Array;
}

export type AssetResolver = (
  ref: string,
  kind: "ambience" | "voice",
) => StereoBuffer | null;

export interface RenderOptions {
  sampleRate?: number;
  assets?: AssetResolver;
}

function dbToAmp(db: number): number {
  return db <= SILENCE_FLOOR_DB ? 0 : Math.pow(10, db / 20);
}

function panGains(pan: number): [number, number] {
  return [Math.min(1, 1 - pan), Math.min(1, 1 + pan)];
}

type ShapeFn = (p: number) => number;

function shapeFor(waveform: Waveform | undefined): ShapeFn {
  switch (waveform ?? "sine") {
    case "sine":
      return (p) => Math.sin(2 * Math.PI * p);
    case "square":
      return (p) => (p < 0.5 ? 1 : -1);
    case "sawtooth":
      return (p) => 2 * p - 1;
    case "triangle":
      // Starts at 0 rising, sine-aligned: 0..1..0..-1..0 over one cycle.
      return (p) => (p < 0.25 ? 4 * p : p < 0.75 ? 2 - 4 * p : 4 * p - 4);
  }
}

/** One voice's ducking window; duck weight w(t) ramps 0->1->0 at the edges. */
interface DuckWindow {
  startT: number;
  endT: number;
  /** Repeat interval in seconds; 0 = plays once. */
  repeatT: number;
  /** Total instance count (1 when not repeating). */
  instances: number;
  duckDb: number;
  targets: ReadonlySet<LayerType>;
}

/** Instances k with startFrame + k*repeatFrames < totalFrames (min 1). */
function voiceInstanceCount(startFrame: number, repeatFrames: number, totalFrames: number): number {
  if (repeatFrames <= 0) return 1;
  return Math.max(1, 1 + Math.floor((totalFrames - 1 - startFrame) / repeatFrames));
}

function duckDbAt(windows: readonly DuckWindow[], type: LayerType, t: number): number {
  let deepest = 0;
  for (const w of windows) {
    if (!w.targets.has(type)) continue;
    if (w.repeatT === 0) {
      if (t <= w.startT || t >= w.endT + DUCK_RAMP_SEC) continue;
      let weight: number;
      if (t < w.startT + DUCK_RAMP_SEC) weight = (t - w.startT) / DUCK_RAMP_SEC;
      else if (t < w.endT) weight = 1;
      else weight = 1 - (t - w.endT) / DUCK_RAMP_SEC;
      const db = w.duckDb * weight;
      if (db < deepest) deepest = db;
      continue;
    }
    // Repeating window: only the few instances whose [s, s+len+ramp) can
    // cover t are inspected — O(overlap), not O(total instances).
    const lenT = w.endT - w.startT;
    const kHi = Math.min(w.instances - 1, Math.floor((t - w.startT) / w.repeatT));
    const kLo = Math.max(0, Math.ceil((t - w.startT - lenT - DUCK_RAMP_SEC) / w.repeatT));
    for (let k = kLo; k <= kHi; k++) {
      const s = w.startT + k * w.repeatT;
      const e = s + lenT;
      if (t <= s || t >= e + DUCK_RAMP_SEC) continue;
      let weight: number;
      if (t < s + DUCK_RAMP_SEC) weight = (t - s) / DUCK_RAMP_SEC;
      else if (t < e) weight = 1;
      else weight = 1 - (t - e) / DUCK_RAMP_SEC;
      const db = w.duckDb * weight;
      if (db < deepest) deepest = db;
    }
  }
  return deepest;
}

interface LayerRenderer {
  addBlock(left: Float64Array, right: Float64Array, startFrame: number, frames: number): void;
  /** Advance sequential state (RNG/filters) without producing output. */
  skip?(frames: number): void;
}

/** Compiled segment: carrier + beat ramps with cumulative boundary phase. */
interface CompiledSegment {
  t0: number;
  t1: number;
  carrier: CompiledRamp;
  beat: CompiledRamp;
  cumCarrier: number;
  cumBeat: number;
}

class ToneLayer implements LayerRenderer {
  private readonly segs: CompiledSegment[];
  private readonly shape: ShapeFn;
  private readonly type: "binaural" | "monaural" | "isochronic";
  private readonly baseAmp: number;
  private readonly panL: number;
  private readonly panR: number;
  private readonly dutyCycle: number;
  private readonly ducks: readonly DuckWindow[];
  private readonly layerType: LayerType;
  private readonly sampleRate: number;
  private segIndex = 0;

  constructor(layer: SegmentedLayer, ducks: readonly DuckWindow[], sampleRate: number) {
    this.type = layer.type;
    this.layerType = layer.type;
    this.shape = shapeFor(layer.waveform);
    this.baseAmp = dbToAmp(layer.gainDb);
    [this.panL, this.panR] = panGains(layer.pan ?? 0);
    this.dutyCycle = layer.type === "isochronic" ? layer.dutyCycle : 0;
    this.ducks = ducks;
    this.sampleRate = sampleRate;

    this.segs = [];
    let cumC = 0;
    let cumB = 0;
    for (const s of layer.segments) {
      const carrier = compileRamp(s.curve, s.carrierHz[0], s.carrierHz[1], s.t0, s.t1);
      const beat = compileRamp(s.curve, s.beatHz[0], s.beatHz[1], s.t0, s.t1);
      this.segs.push({ t0: s.t0, t1: s.t1, carrier, beat, cumCarrier: cumC, cumBeat: cumB });
      cumC += carrier.totalPhase;
      cumB += beat.totalPhase;
    }
  }

  addBlock(left: Float64Array, right: Float64Array, startFrame: number, frames: number): void {
    const sr = this.sampleRate;
    const hasDucks = this.ducks.length > 0;
    for (let i = 0; i < frames; i++) {
      const t = (startFrame + i) / sr;
      while (this.segIndex < this.segs.length - 1 && t >= this.segs[this.segIndex]!.t1) {
        this.segIndex++;
      }
      const seg = this.segs[this.segIndex]!;
      const phiC = seg.cumCarrier + seg.carrier.phaseAt(t);
      const phiB = seg.cumBeat + seg.beat.phaseAt(t);

      let l: number;
      let r: number;
      if (this.type === "binaural") {
        l = this.shape(frac(phiC - phiB / 2));
        r = this.shape(frac(phiC + phiB / 2));
      } else if (this.type === "monaural") {
        const v = 0.5 * (this.shape(frac(phiC - phiB / 2)) + this.shape(frac(phiC + phiB / 2)));
        l = v;
        r = v;
      } else {
        const theta = frac(phiB);
        const gate =
          theta < this.dutyCycle
            ? 0.5 * (1 - Math.cos((2 * Math.PI * theta) / this.dutyCycle))
            : 0;
        const v = this.shape(frac(phiC)) * gate;
        l = v;
        r = v;
      }

      let amp = this.baseAmp;
      if (hasDucks) amp *= dbToAmp(duckDbAt(this.ducks, this.layerType, t));
      left[i]! += l * amp * this.panL;
      right[i]! += r * amp * this.panR;
    }
  }
}

interface EnvPoint {
  t: number;
  gainDb: number;
}

class NoiseLayerR implements LayerRenderer {
  private readonly rng: Xoshiro128pp;
  private readonly color: NoiseLayer["color"];
  private readonly baseAmp: number;
  private readonly env: readonly EnvPoint[] | null;
  private readonly panL: number;
  private readonly panR: number;
  private readonly ducks: readonly DuckWindow[];
  private readonly sampleRate: number;
  private envIndex = 0;
  // Pink (Paul Kellett) + brown filter state.
  private b0 = 0; private b1 = 0; private b2 = 0; private b3 = 0;
  private b4 = 0; private b5 = 0; private b6 = 0;
  private brown = 0;

  constructor(layer: NoiseLayer, ducks: readonly DuckWindow[], sampleRate: number) {
    this.rng = new Xoshiro128pp(fnv1a32(layer.id));
    this.color = layer.color;
    this.baseAmp = dbToAmp(layer.gainDb);
    this.env = layer.envelope ?? null;
    [this.panL, this.panR] = panGains(layer.pan ?? 0);
    this.ducks = ducks;
    this.sampleRate = sampleRate;
  }

  private gainDbAt(t: number): number {
    const env = this.env!;
    while (this.envIndex < env.length - 1 && t >= env[this.envIndex + 1]!.t) {
      this.envIndex++;
    }
    const p = env[this.envIndex]!;
    if (t <= p.t || this.envIndex === env.length - 1) return p.gainDb;
    const next = env[this.envIndex + 1]!;
    const u = (t - p.t) / (next.t - p.t);
    return p.gainDb + (next.gainDb - p.gainDb) * u;
  }

  private nextValue(): number {
    const w = this.rng.nextFloat();
    if (this.color === "white") return w;
    if (this.color === "pink") {
      this.b0 = 0.99886 * this.b0 + w * 0.0555179;
      this.b1 = 0.99332 * this.b1 + w * 0.0750759;
      this.b2 = 0.969 * this.b2 + w * 0.153852;
      this.b3 = 0.8665 * this.b3 + w * 0.3104856;
      this.b4 = 0.55 * this.b4 + w * 0.5329522;
      this.b5 = -0.7616 * this.b5 - w * 0.016898;
      const v = (this.b0 + this.b1 + this.b2 + this.b3 + this.b4 + this.b5 + this.b6 + w * 0.5362) * 0.11;
      this.b6 = w * 0.115926;
      return v;
    }
    this.brown = (this.brown + 0.02 * w) / 1.02;
    return 3.5 * this.brown;
  }

  skip(frames: number): void {
    for (let i = 0; i < frames; i++) this.nextValue();
  }

  addBlock(left: Float64Array, right: Float64Array, startFrame: number, frames: number): void {
    const sr = this.sampleRate;
    const hasDucks = this.ducks.length > 0;
    for (let i = 0; i < frames; i++) {
      const v = this.nextValue();
      const t = (startFrame + i) / sr;
      let amp = this.env ? dbToAmp(this.gainDbAt(t)) : this.baseAmp;
      if (hasDucks && amp > 0) amp *= dbToAmp(duckDbAt(this.ducks, "noise", t));
      left[i]! += v * amp * this.panL;
      right[i]! += v * amp * this.panR;
    }
  }
}

class AmbienceLayerR implements LayerRenderer {
  private readonly asset: StereoBuffer;
  private readonly loop: boolean;
  private readonly baseAmp: number;
  private readonly panL: number;
  private readonly panR: number;
  private readonly ducks: readonly DuckWindow[];
  private readonly sampleRate: number;

  constructor(layer: AmbienceLayer, asset: StereoBuffer, ducks: readonly DuckWindow[], sampleRate: number) {
    this.asset = asset;
    this.loop = layer.loop ?? true;
    this.baseAmp = dbToAmp(layer.gainDb);
    [this.panL, this.panR] = panGains(layer.pan ?? 0);
    this.ducks = ducks;
    this.sampleRate = sampleRate;
  }

  addBlock(left: Float64Array, right: Float64Array, startFrame: number, frames: number): void {
    const len = this.asset.left.length;
    if (len === 0) return;
    const hasDucks = this.ducks.length > 0;
    for (let i = 0; i < frames; i++) {
      const n = startFrame + i;
      const idx = this.loop ? n % len : n;
      if (idx >= len) return;
      let amp = this.baseAmp;
      if (hasDucks) amp *= dbToAmp(duckDbAt(this.ducks, "ambience", n / this.sampleRate));
      left[i]! += this.asset.left[idx]! * amp * this.panL;
      right[i]! += this.asset.right[idx]! * amp * this.panR;
    }
  }
}

class VoiceLayerR implements LayerRenderer {
  private readonly asset: StereoBuffer;
  private readonly startFrame: number;
  private readonly repeatFrames: number;
  private readonly instances: number;
  private readonly baseAmp: number;
  private readonly panL: number;
  private readonly panR: number;

  constructor(layer: VoiceLayer, asset: StereoBuffer, sampleRate: number, totalFrames: number) {
    this.asset = asset;
    this.startFrame = Math.round((layer.startSec ?? 0) * sampleRate);
    this.repeatFrames = layer.repeatEverySec ? Math.round(layer.repeatEverySec * sampleRate) : 0;
    this.instances = voiceInstanceCount(this.startFrame, this.repeatFrames, totalFrames);
    this.baseAmp = dbToAmp(layer.gainDb);
    [this.panL, this.panR] = panGains(layer.pan ?? 0);
  }

  addBlock(left: Float64Array, right: Float64Array, startFrame: number, frames: number): void {
    const len = this.asset.left.length;
    if (len === 0) return;
    if (this.repeatFrames === 0) {
      for (let i = 0; i < frames; i++) {
        const idx = startFrame + i - this.startFrame;
        if (idx < 0) continue;
        if (idx >= len) return;
        left[i]! += this.asset.left[idx]! * this.baseAmp * this.panL;
        right[i]! += this.asset.right[idx]! * this.baseAmp * this.panR;
      }
      return;
    }
    // Repeating: add every instance intersecting this block, k ascending
    // (summation order is part of the cross-engine contract).
    const blockEnd = startFrame + frames;
    const kHi = Math.min(this.instances - 1, Math.floor((blockEnd - 1 - this.startFrame) / this.repeatFrames));
    const kLo = Math.max(0, Math.ceil((startFrame - this.startFrame - len + 1) / this.repeatFrames));
    for (let k = kLo; k <= kHi; k++) {
      const s = this.startFrame + k * this.repeatFrames;
      const from = Math.max(s, startFrame);
      const to = Math.min(s + len, blockEnd);
      for (let n = from; n < to; n++) {
        const i = n - startFrame;
        const idx = n - s;
        left[i]! += this.asset.left[idx]! * this.baseAmp * this.panL;
        right[i]! += this.asset.right[idx]! * this.baseAmp * this.panR;
      }
    }
  }
}

export class Renderer {
  readonly sampleRate: number;
  readonly totalFrames: number;
  private readonly layers: LayerRenderer[];
  private readonly masterAmp: number;
  private readonly fadeInSec: number;
  private readonly fadeOutSec: number;
  private readonly durationSec: number;
  private readonly limiterEnabled: boolean;
  private readonly limiterRelease: number;
  private limiterEnv = 0;
  private cursor = 0;
  // Reused scratch mix buses (float64 for headroom) — grown, never per-block.
  private scratchL = new Float64Array(0);
  private scratchR = new Float64Array(0);

  constructor(preset: Preset, options: RenderOptions = {}) {
    this.sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
    this.durationSec = preset.meta.durationSec;
    this.totalFrames = Math.round(this.durationSec * this.sampleRate);
    this.masterAmp = dbToAmp(preset.master.gainDb);
    this.fadeInSec = preset.master.fadeInSec;
    this.fadeOutSec = preset.master.fadeOutSec;
    this.limiterEnabled = preset.master.limiter;
    this.limiterRelease = Math.exp(-1 / (LIMITER_RELEASE_SEC * this.sampleRate));

    // Build ducking windows from voice layers (needs assets for end times).
    const resolve = (layer: AmbienceLayer | VoiceLayer, kind: "ambience" | "voice"): StereoBuffer => {
      const ref = kind === "ambience" ? (layer as AmbienceLayer).asset : (layer as VoiceLayer).source;
      const asset = options.assets?.(ref, kind) ?? null;
      if (!asset) {
        throw new Error(`unresolvable ${kind} asset "${ref}" — supply an AssetResolver`);
      }
      if (asset.left.length !== asset.right.length) {
        throw new Error(`asset "${ref}" has mismatched channel lengths`);
      }
      return asset;
    };

    const voiceAssets = new Map<VoiceLayer, StereoBuffer>();
    const ducks: DuckWindow[] = [];
    for (const layer of preset.layers) {
      if (layer.type !== "voice") continue;
      const asset = resolve(layer, "voice");
      voiceAssets.set(layer, asset);
      if (layer.ducking) {
        const startT = layer.startSec ?? 0;
        const repeatT = layer.repeatEverySec ?? 0;
        const repeatFrames = repeatT > 0 ? Math.round(repeatT * this.sampleRate) : 0;
        ducks.push({
          startT,
          endT: startT + asset.left.length / this.sampleRate,
          repeatT,
          instances: voiceInstanceCount(
            Math.round(startT * this.sampleRate),
            repeatFrames,
            this.totalFrames,
          ),
          duckDb: layer.ducking.duckDb,
          targets: new Set(layer.ducking.targetLayerTypes),
        });
      }
    }

    this.layers = preset.layers.map((layer: Layer): LayerRenderer => {
      if (isSegmentedLayer(layer)) return new ToneLayer(layer, ducks, this.sampleRate);
      if (layer.type === "noise") return new NoiseLayerR(layer, ducks, this.sampleRate);
      if (layer.type === "ambience") {
        return new AmbienceLayerR(layer, resolve(layer, "ambience"), ducks, this.sampleRate);
      }
      return new VoiceLayerR(layer, voiceAssets.get(layer)!, this.sampleRate, this.totalFrames);
    });
  }

  get position(): number {
    return this.cursor;
  }

  /**
   * Fast-forward to an absolute frame without producing output. Forward-only
   * (recreate the Renderer to seek backward). Tonal layers are O(1) — phase
   * is closed-form — while noise layers advance their RNG/filters so the
   * signal continues exactly as if the skipped span had been rendered. The
   * limiter envelope resets (a documented seek artifact — irrelevant to
   * conformance, which never seeks).
   */
  skipTo(frame: number): void {
    if (frame < this.cursor) {
      throw new Error("skipTo is forward-only — recreate the Renderer to seek backward");
    }
    const delta = frame - this.cursor;
    if (delta === 0) return;
    for (const layer of this.layers) layer.skip?.(delta);
    this.limiterEnv = 0;
    this.cursor = frame;
  }

  /**
   * Render the next `frames` frames into the given output buffers
   * (Float32Array, length >= frames). Frames past the end render silence.
   * Sequential-only: block boundaries never affect the samples produced.
   */
  renderInto(outLeft: Float32Array, outRight: Float32Array, frames: number): void {
    if (this.scratchL.length < frames) {
      this.scratchL = new Float64Array(frames);
      this.scratchR = new Float64Array(frames);
    }
    const mixL = this.scratchL.subarray(0, frames);
    const mixR = this.scratchR.subarray(0, frames);
    mixL.fill(0);
    mixR.fill(0);
    const start = this.cursor;
    const active = Math.max(0, Math.min(frames, this.totalFrames - start));

    if (active > 0) {
      for (const layer of this.layers) {
        layer.addBlock(mixL, mixR, start, active);
      }
    }

    const sr = this.sampleRate;
    const fadeOutStart = this.durationSec - this.fadeOutSec;
    for (let i = 0; i < frames; i++) {
      if (i >= active) {
        outLeft[i] = 0;
        outRight[i] = 0;
        continue;
      }
      const t = (start + i) / sr;
      let g = this.masterAmp;
      if (this.fadeInSec > 0 && t < this.fadeInSec) g *= t / this.fadeInSec;
      if (this.fadeOutSec > 0 && t > fadeOutStart) {
        g *= Math.max(0, (this.durationSec - t) / this.fadeOutSec);
      }
      let l = mixL[i]! * g;
      let r = mixR[i]! * g;

      if (this.limiterEnabled) {
        const peak = Math.max(Math.abs(l), Math.abs(r));
        this.limiterEnv = Math.max(peak, this.limiterEnv * this.limiterRelease);
        if (this.limiterEnv > LIMITER_CEILING) {
          const lim = LIMITER_CEILING / this.limiterEnv;
          l *= lim;
          r *= lim;
        }
      }
      outLeft[i] = l;
      outRight[i] = r;
    }
    this.cursor = start + frames;
  }

  /** Convenience wrapper: allocate and render the next `frames` frames. */
  render(frames: number): StereoBuffer {
    const left = new Float32Array(frames);
    const right = new Float32Array(frames);
    this.renderInto(left, right, frames);
    return { left, right };
  }
}

/** One-shot render of the full preset. */
export function renderAll(preset: Preset, options: RenderOptions = {}): StereoBuffer {
  const renderer = new Renderer(preset, options);
  return renderer.render(renderer.totalFrames);
}
